import React, { useState, useEffect } from "react";
import { DropResult } from "react-beautiful-dnd";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { colors, fonts, shadows, fontWeight } from "src/constants";
import { GlobalState } from "src/reducers";
import {
    InstructionStepState,
    setAnInstructionStepHasMoved,
    setInstructionsSetDeletionStatus,
    setInstructionsSetSubmitStatus,
    setInstructionsStepDeletionIndex,
    setInstructionSteps,
    setInstructionsUnsaved
} from "src/ui/instructions-sets/actions";
import {
    flexRowCenter,
    preventUserInteraction
} from "src/utils/styles/snippets";
import styled, { css } from "styled-components";
import InstructionsFormStepDragArea from "./InstructionsFormStepDragArea";
import { convertToCamelCase } from "src/utils/functions/helpers";
import {
    InstructionElementArrayKey,
    InstructionElementType
} from "src/marketplace/utils/types";
import InstructionsFormStepEmptyDragArea from "./InstructionsFormStepEmptyDragArea";
import {
    convertAPIDataToReduxData,
    instructionElementTitles
} from "src/marketplace/instructions-sets/utils/instructions-sets";
import InstructionStepUploadRequiredElement from "src/marketplace/instructions-sets/components/InstructionStepUploadRequiredElement";
import InstructionsFormStepDragAreaCustomLinkItem from "./InstructionsFormStepDragAreaCustomLinkItem";
import { deleteInstructionStep } from "../actions.instruction-steps";
import { fetchScript } from "src/campaigns/scripts/actions";
import { Descendant } from "slate";
import TextEditor from "src/components/text-editor";
import {
    isContentEmpty,
    serializeContent
} from "src/components/text-editor/utils";
import { getCurrentInstructionsSet } from "../selectors";
import { cloneDeep } from "lodash";
import PlugLinkTooltipMenu from "./PlugLinkTooltipMenu";
import InstructionEditMenu from "src/marketplace/components/InstructionEditMenu";
import InstructionSubmissionTypeSelector from "./InstructionSubmissionTypeSelector";

const Main = styled.section<{ onMainPage: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 12px;
    background-color: ${colors.white};
    border: ${colors.transparent} solid 1px;
    border-radius: 8px;
    box-shadow: ${shadows.four};
    transition: 0.1s ease-in;
    cursor: ${props => (props.onMainPage ? "default" : "grab")};

    ${props =>
        !props.onMainPage &&
        css`
            :active {
                border: ${colors.mediumTeal} solid 1px;
                box-shadow: ${shadows.one};
                cursor: grabbing;
            }

            @media (hover: hover) {
                :hover {
                    border: ${colors.mediumTeal} solid 1px;
                    box-shadow: ${shadows.one};
                }
            }
        `};

    ${props => props.onMainPage && preventUserInteraction()};
`;

const Header = styled.header`
    display: flex;
    align-items: flex-start;
    gap: 12px;
`;

const Position = styled.div`
    ${flexRowCenter()};
    min-width: 24px;
    min-height: 24px;
    color: ${colors.white};
    background-color: ${colors.primaryBlue};
    border-radius: 8px;
    font-size: ${fonts.smallTitle}px;
`;

const DeleteButton = styled.img`
    width: 24px;
    height: 24px;
    opacity: 0.5;
    margin-bottom: 8px;
    cursor: pointer;
`;

const EditorWrapper = styled.div`
    position: relative;
    width: 100%;
    height: auto;
    font-size: ${fonts.smallTitle}px;
    font-weight: ${fontWeight.default};
    text-align: left;
    line-height: 24px;
    transition: 0.1s ease-in;

    ::placeholder {
        color: ${colors.primaryGray};
    }
`;

const toolbarStyles = css`
    margin-bottom: 16px;
    z-index: 2;
`;

const editableStyles = css`
    padding: 6px 12px;
    border-radius: 6px;
    color: ${colors.black};
`;

interface Props {
    myIndex: number;
    myPosition: number;
    description: string;
    dropResult: DropResult | null;
}

const InstructionsFormStep = ({
    myIndex,
    myPosition,
    description,
    dropResult
}: Props) => {
    // Library Methods -----------------------------------------------
    const dispatch = useDispatch();

    // Redux State ---------------------------------------------------
    const {
        instructionsPageType,
        instructionStepDeletionStatus,
        instructionStepDeletionIndex,
        anInstructionStepHasMoved,
        instructionsSetSubmitStatus,
        instructionSteps
    } = useSelector((state: GlobalState) => {
        return state.ui.instructionsSets;
    }, shallowEqual);

    const currentInstructionsSet = useSelector(getCurrentInstructionsSet);

    // Local State ---------------------------------------------------
    const [editorContent, setEditorContent] = useState<Descendant[]>([]);
    const [customLinkUrl, setCustomLinkUrl] = useState<string | null>(null);

    // Local Constants -----------------------------------------------
    const isEditorContentEmpty = isContentEmpty(
        serializeContent(editorContent)
    );

    const currentInstructionStep = instructionSteps[myIndex];

    // Checking if all dropdown areas are filled in with at least one instruction element.
    const instructionStepDropdownItems = [
        "scripts",
        "media",
        "assets",
        "customLink",
        "requiresPlugLink",
        "requiresCustomMedia",
        "requiresScreenshot",
        "requiresLinkSubmission",
        "requiresTextSubmission"
    ] as const;
    type InstructionStepDropdownSection = typeof instructionStepDropdownItems[number];

    const allDragAreasHaveItems =
        instructionStepDropdownItems.filter(
            (item: InstructionStepDropdownSection) => {
                const currentKeyPointsToAScriptsMediaOrAssetsArray =
                    item === "scripts" || item === "media" || item === "assets";

                if (currentKeyPointsToAScriptsMediaOrAssetsArray) {
                    const currentInstructionElement = currentInstructionStep[
                        item
                    ] as number[];
                    return currentInstructionElement.length > 0;
                } else if (item === "customLink") {
                    const currentInstructionElement =
                        currentInstructionStep[item];
                    return (
                        !!currentInstructionElement ||
                        currentInstructionElement === ""
                    );
                } else {
                    const currentInstructionElement =
                        currentInstructionStep[item];

                    return !!currentInstructionElement;
                }
            }
        ).length === instructionStepDropdownItems.length;

    // Local Functions -----------------------------------------------
    function handleDelete(): void {
        if (instructionSteps.length > 1) {
            dispatch(setInstructionsStepDeletionIndex(myIndex));
            dispatch(setInstructionsSetDeletionStatus("prepping for deletion"));
        }
    }

    function _updateInstructionSteps(): void {
        const updatedInstructionSteps = Array.from(instructionSteps);
        const currentStep = updatedInstructionSteps[myIndex];

        // Markdown string used for text editor input.
        const markdownString = serializeContent(editorContent);
        const isMarkdownStringEmpty = isContentEmpty(markdownString);

        if (isMarkdownStringEmpty === false) {
            currentStep.description = markdownString;
        } else {
            currentStep.description = "Please follow the instructions below:";
        }

        if (!!customLinkUrl || customLinkUrl === "") {
            currentStep.customLink = customLinkUrl;
        }
        dispatch(setInstructionSteps(updatedInstructionSteps));
    }

    function _setReorderOnStore(dropResult: DropResult): void {
        // Handling when an instruction step has moved.
        const previousPosition = dropResult.source.index;
        const currentPosition = dropResult.destination?.index || 0;
        const updatedInstructionSteps = Array.from(instructionSteps);
        const [movedInstructionStep] = updatedInstructionSteps.splice(
            previousPosition,
            1
        );
        updatedInstructionSteps.splice(
            currentPosition,
            0,
            movedInstructionStep
        );
        dispatch(setInstructionSteps(updatedInstructionSteps));
    }

    // Effects -------------------------------------------------------
    useEffect(() => {
        // Handling unsaved changes.
        if (currentInstructionsSet) {
            const entitiesStepsAsRedux = convertAPIDataToReduxData(
                currentInstructionsSet
            );
            if (entitiesStepsAsRedux) {
                const _instructionStepsWithoutDescription = cloneDeep(
                    instructionSteps
                ).map((step: Partial<InstructionStepState>) => {
                    delete step["description"];
                    return step;
                });
                const _entitiesStepsWithoutDescription = cloneDeep(
                    entitiesStepsAsRedux
                ).map((step: Partial<InstructionStepState>) => {
                    delete step["description"];
                    return step;
                });
                const _stepDescriptionState = serializeContent(editorContent);

                // TODO: replace with input touched checks
                const descriptionsDoNotMatch = false;
                //     _stepDescriptionState.trim() !==
                //     entitiesStepsAsRedux[myIndex]?.description.trim();
                const entitiesAndReduxStatesNotEqual = false;
                // JSON.stringify(_instructionStepsWithoutDescription) !==
                // JSON.stringify(_entitiesStepsWithoutDescription);

                if (descriptionsDoNotMatch) {
                    dispatch(setInstructionsUnsaved(true));
                } else {
                    dispatch(
                        setInstructionsUnsaved(entitiesAndReduxStatesNotEqual)
                    );
                }
            }
        }
    }, [
        editorContent,
        currentInstructionsSet,
        instructionSteps,
        currentInstructionStep
    ]);

    useEffect(() => {
        // Fetching media and assets related to scripts within the step on initial load for viewing and editing purposes.
        const currentInstructionStepScriptIds = currentInstructionStep.scripts;
        currentInstructionStepScriptIds.forEach((id: number) => {
            dispatch(fetchScript(id));
        });
    }, [currentInstructionStep]);

    useEffect(() => {
        if (anInstructionStepHasMoved) {
            _updateInstructionSteps();
            if (!!dropResult) _setReorderOnStore(dropResult);
            dispatch(setAnInstructionStepHasMoved(false));
        }
    }, [anInstructionStepHasMoved]);

    useEffect(() => {
        // Handling instruction step deletion
        if (instructionStepDeletionStatus === "prepping for deletion") {
            const updatedInstructionSteps = Array.from(instructionSteps);
            const currentStep = updatedInstructionSteps[myIndex];
            currentStep.description = description;
            if (!!customLinkUrl || customLinkUrl === "") {
                currentStep.customLink = customLinkUrl;
            }
            dispatch(setInstructionSteps(updatedInstructionSteps));
            dispatch(setInstructionsSetDeletionStatus("deleting"));
        } else if (
            instructionStepDeletionStatus === "deleting" &&
            instructionStepDeletionIndex === myIndex
        ) {
            // Updating UI after removing instruction.
            let updatedInstructionSteps = Array.from(instructionSteps);
            const deletedStep = updatedInstructionSteps.splice(myIndex, 1);
            if (currentInstructionStep.requiresCustomMedia) {
                updatedInstructionSteps = updatedInstructionSteps.filter(
                    (step, idx) => {
                        if (step.requiresRawMedia) {
                            if (step.id)
                                dispatch(deleteInstructionStep(step.id));
                            return false;
                        } else return true;
                    }
                );
            }
            if (updatedInstructionSteps.length === 0)
                updatedInstructionSteps[0] = {
                    ...deletedStep[0],
                    scripts: [],
                    media: [],
                    assets: [],
                    requiresPlugLink: false,
                    requiresScreenshot: false,
                    requiresTextSubmission: false,

                    requiresCustomMedia: false,
                    requiresRawMedia: false
                };
            dispatch(setInstructionSteps(updatedInstructionSteps));

            // If the deleted instruction step exists in the backend, delete it from there as well.
            if (instructionsPageType === "Edit" && !!deletedStep[0].id) {
                const deletedInstructionStepId = deletedStep[0].id;
                dispatch(deleteInstructionStep(deletedInstructionStepId));
            }
            // Resetting deletion flow
            dispatch(setInstructionsSetDeletionStatus("not deleting"));
        }
    }, [instructionStepDeletionStatus]);

    useEffect(() => {
        // Handling when a new instruction step needs to be added or an instruction set is submitted.
        if (
            instructionsSetSubmitStatus ===
            "updating data for adding new instruction"
        ) {
            _updateInstructionSteps();
            dispatch(
                setInstructionsSetSubmitStatus(
                    "finish updating data for adding new instruction"
                )
            );
        } else if (
            instructionsSetSubmitStatus === "updating data to send to api"
        ) {
            _updateInstructionSteps();
            dispatch(setInstructionsSetSubmitStatus("submitting to api"));
        }
    }, [instructionsSetSubmitStatus]);

    // JSX -----------------------------------------------------------
    return (
        <Main onMainPage={instructionsPageType === "Main"}>
            <Header>
                <EditorWrapper>
                    <TextEditor
                        initialText={description}
                        editorContent={editorContent}
                        setEditorContent={setEditorContent}
                        handleChange={(newContent: Descendant[]) => {
                            setEditorContent(newContent);
                            // dispatch(setInstructionsUnsaved(true));
                        }}
                        setIsError={() => true}
                        leftComponent={<Position>{myPosition}</Position>}
                        rightComponent={
                            instructionsPageType !== "Main" && (
                                <InstructionEditMenu
                                    handleDelete={handleDelete}
                                    instruction={currentInstructionStep}
                                />
                            )
                        }
                        toolbarStyles={
                            instructionsPageType === "Main"
                                ? { display: "none" }
                                : toolbarStyles
                        }
                        editableStyles={editableStyles}
                        placeholder="Click here to input your instruction text"
                        addFocusError={isEditorContentEmpty}
                        addFocus
                    />
                </EditorWrapper>
            </Header>

            {/* Rendering script, media, and asset items --------------------------------------------------------------- */}
            {instructionElementTitles
                .slice(0, 3)
                .map((title: InstructionElementType) => {
                    const currentInstructionStepKey = convertToCamelCase(
                        title
                    ) as InstructionElementArrayKey;

                    const currentInstructionStepElementIds =
                        currentInstructionStep[currentInstructionStepKey];

                    const hasElements =
                        currentInstructionStepElementIds?.length > 0;

                    return (
                        <InstructionsFormStepDragArea
                            key={`instructions-form-step-drag-area-${title}-${myIndex}`}
                            instructionSteps={instructionSteps}
                            currentInstructionStepIndex={myIndex}
                            currentInstructionStepKey={
                                currentInstructionStepKey
                            }
                            currentInstructionStepElementIds={
                                currentInstructionStepElementIds
                            }
                            hasElements={hasElements}
                            title={title}
                        />
                    );
                })}

            {/* Rendering link items --------------------------------------------------------------- */}
            {currentInstructionStep.customLink !== null && (
                <InstructionsFormStepDragAreaCustomLinkItem
                    key="instructions-form-step-drag-area-custom-link"
                    instructionSteps={instructionSteps}
                    currentInstructionStepIndex={myIndex}
                    customLinkUrl={customLinkUrl || "https://"}
                    setCustomLinkUrl={setCustomLinkUrl}
                />
            )}
            {currentInstructionStep.requiresPlugLink && (
                <InstructionStepUploadRequiredElement
                    key="instructions-form-step-drag-area-requires-plug-link"
                    title="Generate Plug Link"
                    text="The influencer will receive an auto-generated Plug Link for them to share."
                    instructionSteps={instructionSteps}
                    currentInstructionStepIndex={myIndex}
                    toolTipContent={
                        <PlugLinkTooltipMenu
                            currentInstructionStepIndex={myIndex}
                        />
                    }
                    isPlugLinkSubmission
                />
            )}
            <InstructionSubmissionTypeSelector
                currentInstructionStep={currentInstructionStep}
                instructionSteps={instructionSteps}
                currentInstructionStepIndex={myIndex}
            />
            {/* Catch-All Empty Drag Area --------------------------------------------------------------- */}
            {!allDragAreasHaveItems && instructionsPageType !== "Main" && (
                <InstructionsFormStepEmptyDragArea
                    instructionStepIndex={myIndex}
                />
            )}
        </Main>
    );
};

export default InstructionsFormStep;
